import axios, { AxiosError, CanceledError, type InternalAxiosRequestConfig } from "axios";
import type { RefreshTokenResponse } from "dutyon-server/types";
import { useAuthStore } from "../auth/authStore";
import { ClientOfflineError, isClientOnline } from "../logging/client-error";
import { createClientRequestId } from "../logging/client-log-policy";
import { logClientError, logClientWarning } from "../logging/client-logging";
import { normalizeApiBaseUrl } from "./api-url";

const baseURL = normalizeApiBaseUrl(import.meta.env.API_URL);

export const httpClient = axios.create({
  baseURL: `${baseURL}/api`,
  timeout: 30_000,
  headers: {
    "Content-Type": "application/json",
  },
});

export { AxiosError };

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

let refreshRequest: Promise<RefreshTokenResponse> | null = null;

function canceledRequest(config: InternalAxiosRequestConfig) {
  const error = new CanceledError();
  error.config = config;
  return error;
}

function getErrorRequestId(error: AxiosError) {
  const responseRequestId = error.response?.headers["x-request-id"];
  if (typeof responseRequestId === "string") return responseRequestId;

  const requestRequestId = error.config?.headers?.get?.("X-Request-ID");
  return typeof requestRequestId === "string" ? requestRequestId : undefined;
}

export const refreshAccessToken = (refreshToken: string) => {
  if (!refreshRequest) {
    const requestId = createClientRequestId();
    refreshRequest = axios
      .post<RefreshTokenResponse>(
        `${baseURL}/api/auth/refresh`,
        { refresh_token: refreshToken },
        {
          timeout: 15_000,
          headers: requestId ? { "X-Request-ID": requestId } : undefined,
        },
      )
      .then((response) => response.data)
      .finally(() => {
        refreshRequest = null;
      });
  }

  return refreshRequest;
};

httpClient.interceptors.request.use(
  (config) => {
    if (!isClientOnline()) {
      return Promise.reject(new ClientOfflineError());
    }
    if (!config.headers.has("X-Request-ID")) {
      const requestId = createClientRequestId();
      if (requestId) config.headers.set("X-Request-ID", requestId);
    }
    const token = useAuthStore.getState().token;
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

httpClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;
    if (originalRequest?.signal?.aborted) {
      return Promise.reject(canceledRequest(originalRequest));
    }
    const isAuthenticationRequest =
      originalRequest?.url?.startsWith("/auth/") ||
      originalRequest?.url?.startsWith("/public/") ||
      false;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthenticationRequest
    ) {
      originalRequest._retry = true;
      const refreshToken = useAuthStore.getState().refresh_token;

      if (refreshToken) {
        try {
          const tokens = await refreshAccessToken(refreshToken);
          useAuthStore.getState().setToken(tokens.access_token, tokens.refresh_token);

          // 공유 갱신은 유지하되 취소된 요청은 다시 전송하지 않는다.
          if (originalRequest.signal?.aborted) {
            return Promise.reject(canceledRequest(originalRequest));
          }

          // 새 토큰으로 원래 요청 재시도
          originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`;
          return httpClient(originalRequest);
        } catch (refreshError: unknown) {
          const status = axios.isAxiosError(refreshError)
            ? refreshError.response?.status
            : undefined;
          const rejectedSession = status === 401 || status === 403;
          logClientWarning(
            rejectedSession
              ? "인증 세션이 거부되어 로그아웃 처리했습니다."
              : "인증 세션을 갱신하지 못했습니다. 연결을 확인한 뒤 다시 시도해 주세요.",
            {
              action: "인증 세션 갱신",
              errorCode: status ? `HTTP_${status}` : "REFRESH_UNAVAILABLE",
              requestId: axios.isAxiosError(refreshError)
                ? getErrorRequestId(refreshError)
                : undefined,
            },
          );
          if (rejectedSession) useAuthStore.getState().clearToken();
          // 일시적 실패는 세션을 보존하고 실제 갱신 오류를 전달한다.
          if (originalRequest.signal?.aborted) {
            return Promise.reject(canceledRequest(originalRequest));
          }
          return Promise.reject(refreshError);
        }
      } else {
        logClientWarning("인증 세션 갱신 정보가 없어 로그아웃 처리했습니다.", {
          action: "인증 세션 갱신",
          errorCode: "REFRESH_TOKEN_MISSING",
          requestId: getErrorRequestId(error),
        });
        useAuthStore.getState().clearToken();
      }
    }
    if (axios.isCancel(error)) return Promise.reject(error);
    if (!error.response) {
      logClientWarning("서버와 통신할 수 없어 API 요청이 실패했습니다.", {
        action: "API 네트워크 요청",
        errorCode: error.code || "NETWORK_ERROR",
        requestId: getErrorRequestId(error),
      });
    } else if (error.response.status >= 500) {
      logClientError("서버 오류로 API 요청이 실패했습니다.", {
        action: "API 응답 처리",
        errorCode: `HTTP_${error.response.status}`,
        requestId: getErrorRequestId(error),
      });
    }
    return Promise.reject(error);
  },
);
